import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { Form, Formik } from "formik";
import { DrawerForm } from "../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { ModalActionButtonStyles } from "../Common/styles/modal";

type Props = {
  closeDrawer: () => void;
  refreshPage: () => void;
  data: any;
};

let schema = yup.object().shape({
  siteName: yup
    .string()
    .trim()
    .required("Site Name is Required")
    .max(100, "Site Name cannot be greater than 100 Characters"),
  siteAbbreviation: yup.string().required("Site Abbreviation is Required"),
  countryId: yup.string().required("Country is Required"),
  // siteCode: yup.string().required("Site Code is Required"),
});

const AddSiteDrawer = ({ closeDrawer, refreshPage, data }: Props) => {
  const [countriesList, setCountriesList] = useState<any[]>([]);

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/data/countries`);
        const newData = res?.data?.data?.map((country: any) => ({
          value: country?.valueId,
          label: country?.label,
        }));
        setCountriesList(newData);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.siteName,
        abbreviation: values?.siteAbbreviation,
        countryId: values?.countryId,
        // code: values?.siteCode,
      };
      let res: AxiosResponse;
      if (data?.id) {
        res = await http.put(`/registry/sites/${data?.id}`, body);
      } else {
        res = await http.post(`/registry/sites`, body);
      }

      toastMessage("success", res?.data?.message);
      closeDrawer();
      refreshPage();
      setSubmitLoader(false);
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };
  return (
    <Box
      sx={{
        width: "900px",
        p: 2.5,
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "25px",
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
          Add a New Site
        </Typography>
        <IconButton onClick={closeDrawer}>
          <CloseRounded sx={{ color: "text.secondary" }} />
        </IconButton>
      </Box>
      <Divider sx={{ mt: 2, mb: 3 }} />
      <Box
        sx={{
          width: "100%",
          height: "calc(100% - 66px)",
          overflow: "auto",
        }}
      >
        {!loading ? (
          <Formik
            initialValues={{
              siteName: data?.siteName || "",
              siteAbbreviation: data?.abbreviation || "",
              countryId: data?.countryId || "",
              // siteCode: data?.siteCode || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ getFieldProps, errors, touched, setFieldValue, values }) => (
              <Form style={DrawerForm}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-name">
                    Site Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Site name"
                    id="site-name"
                    inputProps={{
                      maxLength: 100,
                    }}
                    {...getFieldProps("siteName")}
                    error={touched?.siteName && errors?.siteName ? true : false}
                    helperText={
                      touched?.siteName && errors?.siteName
                        ? (errors?.siteName as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="country-id">
                    Country <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    value={values.countryId}
                    onChange={(e) => {
                      setFieldValue("countryId", e.target.value);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: "300px", overflowY: "auto" },
                      },
                    }}
                    fullWidth
                    id="country-id"
                  >
                    {countriesList.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={
                      touched?.countryId && errors?.countryId ? true : false
                    }
                  >
                    {touched?.countryId && errors?.countryId
                      ? (errors?.countryId as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                {/* <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="site-code">
                  Site Code
                </FormLabel>
                <TextField
                  placeholder="Site code"
                  id="site-code"
                  {...getFieldProps("siteCode")}
                  error={touched?.siteCode && errors?.siteCode ? true : false}
                  helperText={
                    touched?.siteCode && errors?.siteCode
                      ? (errors?.siteCode as string)
                      : " "
                  }
                  inputProps={{ maxLength: 6 }}
                />
              </FormControl> */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-abbreviation">
                    Site Abbreviation <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Site abbreviation"
                    id="site-abbreviation"
                    {...getFieldProps("siteAbbreviation")}
                    error={
                      touched?.siteAbbreviation && errors?.siteAbbreviation
                        ? true
                        : false
                    }
                    helperText={
                      touched?.siteAbbreviation && errors?.siteAbbreviation
                        ? (errors?.siteAbbreviation as string)
                        : " "
                    }
                    inputProps={{ maxLength: 6 }}
                  />
                </FormControl>
                <Box sx={{ ...ModalActionButtonStyles, mt: "auto" }}>
                  {!submitLoader ? (
                    <Button type="submit" variant="contained">
                      {data?.id ? "Update" : "Create"}
                    </Button>
                  ) : (
                    <Box sx={{ p: 1 }}>
                      <CircularProgress size={25} />
                    </Box>
                  )}
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CircularProgress size={25} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddSiteDrawer;
